import {
  Box,
  styled,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  SelectChangeEvent,
  TextField,
  Button,
} from '@mui/material';
import { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { useEnv } from 'src/context/EnvProvider';
import useLoadDataList from 'src/hook/useLoadDataList';
import { getAllAssemblies } from 'src/services/assemblies';
import { getGranits } from 'src/services/granits';
import {
  addStock,
  GetPieceUnderAssembly,
  getPiecesUnderAssemblies,
  GetPiece,
} from 'src/services/stock';
import { handleMutation } from 'src/utils/api.utils';

export const AddUnderAssemblyStock = () => {
  const { data: granits } = useLoadDataList(getGranits);
  const { buckets } = useEnv();

  const [selectedUnderAssemblyId, setSelectedUnderAssemblyId] = useState<string>();
  const [selectedGranits, setSelectedGranits] = useState<{ pieceId: number; granitId: string }[]>(
    [],
  );

  const [selectedUnderAssembly, setSelectedUnderAssembly] = useState<GetPieceUnderAssembly>();

  const {
    data: underAssemblies,
    onRefresh,
    refreshing: isLoadingStockPieces,
  } = useLoadDataList(() => getPiecesUnderAssemblies());

  const [quantity, setQuantity] = useState('');
  const [price, setPrice] = useState('');
  const [name, setName] = useState('');

  const handleUnderAssemblyChange = (event: SelectChangeEvent) => {
    setSelectedUnderAssemblyId(event.target.value);
    const underAssembly = underAssemblies?.find((p) => p.id === Number(event.target.value));

    setSelectedUnderAssembly(underAssembly);

    setName(underAssembly?.name || '');
    onRefresh();
  };

  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault();

    if (!selectedUnderAssembly) return;

    const pieces = selectedUnderAssembly.pieces.map((piece) => {
      const selectedGranit = selectedGranits.find((g) => g.pieceId === piece.id);

      return {
        pieceId: piece.id,
        granitId: selectedGranit?.granitId || '',
        newGeometry: [],
      };
    });

    handleMutation(
      (data) => addStock(data),
      {
        name,
        quantity: Number.parseInt(quantity, 10),
        price: Number.parseFloat(price),
        stockPieces: pieces,
      },
      () => {
        toast.success('Stock ajouté avec succès');
        onRefresh();
      },
    );
  };

  return (
    <Container>
      <Form onSubmit={handleSubmit}>
        <FormControl fullWidth>
          <FormControl sx={{ width: '100%' }}>
            <InputLabel id="piece-label">Choisir un sous assemblage</InputLabel>
            <Select
              labelId="piece-label"
              id="piece-select"
              value={selectedUnderAssemblyId}
              label="Pièce"
              onChange={handleUnderAssemblyChange}
              required
              renderValue={(value) => {
                const underAssembly = underAssemblies?.find((p) => p.id === Number(value));
                return underAssembly?.name;
              }}
            >
              {underAssemblies?.map((underAssembly) => {
                const image = underAssembly.pictureUrl;

                return (
                  <MenuItem
                    key={underAssembly.id}
                    value={underAssembly.id.toString()}
                    sx={{ display: 'flex', alignItems: 'center', gap: 2, flexDirection: 'row' }}
                  >
                    {image && (
                      <Box
                        component="img"
                        src={`${buckets?.BUCKET_3D_PIECES || ''}/${image}`}
                        alt={underAssembly.name}
                        sx={{
                          width: 75,
                          height: 75,
                          objectFit: 'cover',
                          backgroundColor: '#F3F4F7',
                        }}
                      />
                    )}
                    <Typography variant="body1">{underAssembly.name}</Typography>
                  </MenuItem>
                );
              })}
            </Select>
          </FormControl>

          {selectedUnderAssembly &&
          selectedUnderAssembly.pieces.length > 0 &&
          !isLoadingStockPieces ? (
            <div>
              <Typography variant="subtitle1" gutterBottom>
                Pièces du sous assemblage
              </Typography>
              {selectedUnderAssembly.pieces.map((piece) => {
                const handleChangeGranit = (e: SelectChangeEvent) => {
                  const granit = selectedGranits?.find(
                    (g) => g.granitId === e.target.value && g.pieceId === piece.id,
                  );

                  if (granit) {
                    setSelectedGranits(selectedGranits.filter((g) => g.pieceId !== piece.id));
                  } else {
                    setSelectedGranits([
                      ...selectedGranits,
                      { pieceId: piece.id, granitId: e.target.value },
                    ]);
                  }
                };

                const selectedGranit = selectedGranits.find((g) => g.pieceId === piece.id);

                return (
                  <div
                    key={piece.id}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      gap: 2,
                      marginBottom: '24px',
                    }}
                  >
                    <Row>
                      {piece?.pictureUrl && (
                        <Box
                          component="img"
                          src={`${buckets?.BUCKET_3D_PIECES || ''}/${piece.pictureUrl}`}
                          alt={piece.name}
                          sx={{ width: 50, height: 50, objectFit: 'cover' }}
                        />
                      )}

                      <Typography variant="body1">{piece?.name}</Typography>
                    </Row>
                    <FormControl sx={{ minWidth: 200 }}>
                      <InputLabel id={`granit-label-${piece.id}`}>Granit</InputLabel>
                      <Select
                        sx={{ width: '300px' }}
                        labelId={`granit-label-${piece.id}`}
                        value={selectedGranit?.granitId}
                        onChange={handleChangeGranit}
                        label="Granit"
                        required
                        renderValue={(value) => {
                          const granit = granits?.find((g) => g.id === value);
                          return granit?.name;
                        }}
                      >
                        {granits
                          ?.filter((g) => {
                            if (piece?.pieceFabrication) {
                              const fabricationPiece = piece?.pieceFabrication.find(
                                (p) => p.fabrication === g.fabrication,
                              );
                              return !!fabricationPiece;
                            }
                            return g.fabrication === 'FRENCH';
                          })
                          .map((granit) => (
                            <MenuItem
                              key={granit.id}
                              value={granit.id}
                              sx={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 2,
                                flexDirection: 'row',
                              }}
                            >
                              {granit.image && (
                                <Box
                                  component="img"
                                  src={`${buckets?.BUCKET_3D_PIECES || ''}/${granit.image}`}
                                  alt={granit.name}
                                  sx={{ width: 50, height: 50, objectFit: 'cover' }}
                                />
                              )}
                              <Typography variant="body1">{granit.name}</Typography>
                            </MenuItem>
                          ))}
                      </Select>
                    </FormControl>
                  </div>
                );
              })}
            </div>
          ) : null}
        </FormControl>

        <TextField
          fullWidth
          label="Quantité"
          type="number"
          value={quantity}
          onChange={(e) => setQuantity(e.target.value)}
          required
          inputProps={{ min: 1 }}
        />

        <TextField
          fullWidth
          label="Prix"
          type="number"
          value={price}
          onChange={(e) => setPrice(e.target.value)}
          required
          inputProps={{ min: 0, step: 0.01 }}
        />

        <TextField
          fullWidth
          label="Nom"
          type="text"
          value={name}
          onChange={(e) => setName(e.target.value)}
          required
        />

        <Button type="submit" variant="contained" color="primary" fullWidth>
          Ajouter au stock
        </Button>
      </Form>
    </Container>
  );
};

const Container = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  gap: 16,
});

const Form = styled('form')({
  display: 'flex',
  flexDirection: 'column',
  gap: 16,
});

const Row = styled('div')({
  display: 'flex',
  alignItems: 'center',
  gap: 16,
});
